/**
 * The settlement's ground, derived from the field instead of stamped on it.
 *
 * The campaign chart stopped being a diagram when `ui/relief.ts` lit a height
 * field the generator had always had. This asks the settlement the same
 * question — *what does `sim/map.ts` know about this ground that the renderer
 * never drew?* — and the answer turned out to be almost everything:
 *
 *   • A **stone outcrop stands up.** A ditch is a hole. A river is the bottom
 *     of a cut and its banks are the sides of one. Marsh lies in the low
 *     ground. None of that was ever *state*; it is implied exactly by the
 *     terrain class and can be derived from it for nothing. The settlement map
 *     has no elevation array and does not need one — it has twelve terrain
 *     classes, each of which is a statement about height.
 *   • The **edge between two classes is a transition.** The renderer drew one
 *     of four pre-baked 32-pixel tiles per terrain, butted edge to edge, so
 *     grass met forest floor on a straight line down a tile boundary and the
 *     ground read as a tiled floor. It is now a blur with a per-surface
 *     hardness, so the wood shades into the plain over a tile and a road does
 *     not.
 *   • The **country** it stands in — see `biome.ts`.
 *
 * ## The two layers, and why they are two
 *
 * Exactly the split `ui/relief.ts` argued for and for the same reasons. This
 * file bakes the **form**: colour, hillshade and the coarse grain of the
 * surface, at six pixels to a tile, resampled up by the GPU. `textures.ts`
 * bakes the **marks**: crisp tufts, pebbles, reeds, planks and ripples at
 * thirty-two pixels to a tile, drawn over the top. Smooth things and sharp
 * things blend differently and want different pitches, and every attempt to do
 * both in one layer has come out either soft or noisy.
 *
 * ## Why it is pure, and what that buys
 *
 * No Pixi, no canvas, no DOM: a plain `Uint8ClampedArray` in, a plain
 * `Uint8ClampedArray` out. `scene.ts` owns the one canvas this is written into
 * and the texture it becomes. That is what lets `tests/cityart.test.ts` assert
 * the luma cap, the determinism and the chunk-seam continuity in a headless
 * test runner instead of in a promise.
 *
 * **Determinism matters here for a reason that is not the usual one.** Nothing
 * in this file can change the simulation — it cannot even see it, it is handed
 * a copy of the terrain array. But a chunk is baked when the camera reaches it
 * and re-baked when a road is laid, so the *same* ground is baked many times
 * over a session, and a bake that depended on anything but position would make
 * the ground flicker as the player panned. Everything here is a pure function
 * of the global tile coordinate and the world's own seed.
 */

import { hash2 } from '../core/math.ts';
import { mix } from '../data/palette.ts';
import { Terrain } from '../sim/types.ts';
import {
  TERRAIN_MAX_LUMA,
  capLuma,
  type BiomeTheme,
  type FabricKind,
  type Surface,
  surfaceTable,
} from './biome.ts';

/**
 * The brightest the *lamp* may be, as luma over 0..255.
 *
 * **A light brighter than the ground is allowed to be cannot light anything.**
 * The themes name their sun as a colour of daylight — `0xd8cdb6` is luma 205,
 * which is what a bone-white winter sky looks like — and mixing 40% of that
 * into a stone shelf at luma 46 gives 110, half again over `TERRAIN_MAX_LUMA`.
 * The cap then scales it back to 78, and so does the pixel next to it, and the
 * one after that: **the whole lit face lands on the ceiling and comes out as a
 * flat pale plate with no form left in it.** Measured on `cityart-03-seams-z62`
 * as a two-and-a-half-tile band of warm haze beside every shelf and every bank,
 * which read as smoke; and it is the same saturation failure the campaign
 * chart's hillshade spent two attempts on, arrived at from the other end.
 *
 * 110 is chosen from the arithmetic rather than by eye: the brightest surface
 * in the table sits near luma 46, the ceiling is 78, and the light may push a
 * pixel at most half way — `46 + 0.5 × (110 − 46) = 78`. So the steepest lit
 * face in the game arrives *at* the cap and everything shallower arrives under
 * it, which is exactly the condition for the cap never to be reached twice in
 * a row. The hue is untouched: `capLuma` scales rather than clamps.
 */
const SUN_MAX_LUMA = 110;

/**
 * Ground pixels to a tile.
 *
 * Six, and the number was measured rather than picked. This layer is a *form*
 * layer — a hillshade over a field that is smooth at about two tiles, plus the
 * country's own grain — and the GPU resamples it up to the 512-pixel chunk with
 * linear filtering, which is the right answer for a hillshade and the wrong one
 * for a mark. The marks are the tile textures' layer, drawn crisp at five times
 * this pitch.
 *
 * **The first cut was four, and the picture said no.** At four the finest grain
 * the sampling rate allows is one feature per tile, and a feature a tile wide
 * blurred over eight screen pixels apiece is not ground, it is weather: the
 * open country came out as a soft warm haze at the zoom a player actually
 * plays at. Six gives a Nyquist of three features per tile and `FABRIC_FINEST`
 * takes half of that, which is a grain of about half a tile — nineteen screen
 * pixels at the default zoom, and it reads as ground.
 *
 * The cost is quadratic in this number and it is the whole cost of the layer:
 * 9,216 pixels of JavaScript per chunk against four's 4,096, measured at
 * 1.4–2.0 ms per chunk against 0.6–1.1. Eight was tried and is 3.5 ms, which
 * is a dropped frame every time a laid road re-bakes what is on screen.
 */
export const GROUND_SUB = 6;

/**
 * Tiles of margin read outside the chunk.
 *
 * Two, and it is exactly two: the blur is a 3×3 over the tile grid, and the
 * smooth interpolation that follows samples between tile *centres*, so a pixel
 * on the chunk's own edge needs a blurred value one tile outside it, which
 * needs a raw value two tiles outside it. One less and every chunk boundary
 * gets a seam; one more is a slower bake for nothing.
 */
const MARGIN = 2;

/**
 * Where the light comes from: north-west.
 *
 * The same corner as `drawBuilding`'s lit face, `curtainart.ts`'s lit tower
 * side, the resource icons and the campaign chart's hillshade. This is the one
 * constant in the whole renderer that everything else has already agreed on,
 * and ground lit from anywhere else would be the surface that disagrees.
 */
const LIGHT_X = -0.7071;
const LIGHT_Y = -0.7071;

/**
 * How far the slope is pushed before it is lit.
 *
 * The gradient is measured per ground pixel, which is a quarter of a tile, so
 * the `GROUND_SUB` factor converts it to tiles-per-tile first and this is the
 * honest vertical exaggeration on top. Relief maps all have one; the question
 * is only how much, and the chart's own hard-won lesson applies unchanged —
 * **keeping the light out of saturation is the whole game**, because a region
 * that has reached ±1 has no variation left in it. At 1.6 a stone shelf's
 * half-tile rise over the blur arrives as a highlight of about 0.3, which
 * leaves most of the range for the country's own relief above it.
 *
 * Saturation of the *slope* is only half of it, and the half that actually
 * shipped a fault was the other one — see `SUN_MAX_LUMA`. A surface can be well
 * short of a vertical normal and still be lit clean through the colour ceiling,
 * which looks identical and is a different bug.
 */
const EXAGGERATION = 1.6;

/**
 * The fabric's finest feature, in features per tile.
 *
 * Pinned to this bake's own resolution and for the third time in this project:
 * `ui/relief.ts` made the mistake once in each direction and wrote the ceiling
 * down rather than have it rediscovered. At `GROUND_SUB` of 6 anything past 3.0
 * is finer than the grid, and 1.5 leaves four samples to a feature, which is
 * where a texture stops shimmering as the camera moves.
 *
 * **And there is a floor as well, which is the half nobody writes down.** The
 * frequencies below all had to be doubled from the campaign chart's, because a
 * chart cell is two map units on a board seen whole and a settlement tile is
 * thirty screen pixels seen from ten feet away. Translating the chart's numbers
 * unchanged gave features eight tiles across, which at the settlement's zoom is
 * not a texture at all — it is fog.
 */
const FABRIC_FINEST = 1.5;

/** How far the shapers' output is stretched about its own mean. See `fbm`. */
const FABRIC_CONTRAST = 2.1;

/**
 * The hardest the fabric may ever darken or lighten the ground, 0..1.
 *
 * The chart learned this the expensive way — near-black inks and unclamped
 * ramps gave the owner "random black shadow spots on the map" — and the fix
 * that held was a single cap at the layer rather than discipline at nine call
 * sites. Same here, and lower, because this ground is already the darkest
 * surface in the game before anything is laid over it.
 *
 * **0.20, down from 0.28, and the reason is the resolution rather than the
 * taste.** This layer is six pixels to a tile; at the close zoom the shots take
 * that is ten screen pixels to a ground pixel, so a *stain* laid here arrives
 * as a wisp about the size of a man — which is what made a stone shelf read as
 * smoke in `cityart-03-seams-z62`. The part of a fabric that survives the
 * upsampling honestly is its `lift`, because that goes through the light and a
 * lit form is smooth by nature. So the fabric shades less and shapes the same,
 * and the crisp half of every surface is the marks layer's, at five times the
 * pitch, where it belongs.
 */
const FABRIC_MAX = 0.2;

/** Smooth value noise in tile space. `f` is features per tile. */
function vn(x: number, y: number, f: number, seed: number): number {
  const px = x * f;
  const py = y * f;
  const x0 = Math.floor(px);
  const y0 = Math.floor(py);
  const tx = px - x0;
  const ty = py - y0;
  const sx = tx * tx * (3 - 2 * tx);
  const sy = ty * ty * (3 - 2 * ty);
  const a = hash2(x0, y0, seed);
  const b = hash2(x0 + 1, y0, seed);
  const c = hash2(x0, y0 + 1, seed);
  const d = hash2(x0 + 1, y0 + 1, seed);
  return (a + (b - a) * sx) * (1 - sy) + (c + (d - c) * sx) * sy;
}

/**
 * Fractal Brownian motion — for anything whose structure is blotchy.
 *
 * The contrast stretch on the way out is not a flourish and the chart has the
 * measurement: averaging octaves pulls the result hard toward 0.5, so thresholds
 * written against single-octave noise fire on a seventh of the ground at a
 * seventh of the strength. Stretching about the midpoint puts the range back
 * where the thresholds expect it, in one place instead of nine.
 */
function fbm(x: number, y: number, f: number, seed: number, octaves = 3): number {
  let sum = 0;
  let amp = 1;
  let norm = 0;
  let freq = f;
  for (let o = 0; o < octaves; o++) {
    if (freq > FABRIC_FINEST) break;
    sum += vn(x, y, freq, seed + o * 31) * amp;
    norm += amp;
    amp *= 0.5;
    freq *= 2;
  }
  if (norm <= 0) return 0.5;
  const v = 0.5 + (sum / norm - 0.5) * FABRIC_CONTRAST;
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** The same, folded — for surfaces whose grain runs in lines rather than lumps. */
function ridged(x: number, y: number, f: number, seed: number, octaves = 2): number {
  let sum = 0;
  let amp = 1;
  let norm = 0;
  let freq = f;
  for (let o = 0; o < octaves; o++) {
    if (freq > FABRIC_FINEST) break;
    const v = 1 - Math.abs(vn(x, y, freq, seed + o * 37) - 0.5) * 2;
    sum += v * v * amp;
    norm += amp;
    amp *= 0.5;
    freq *= 2;
  }
  if (norm <= 0) return 0.33;
  // Folded noise squared sits low — its mean is about a third — so it is
  // stretched from *its* centre, or every lineated surface comes out uniformly
  // dark instead of uniformly creased.
  const v = 0.33 + (sum / norm - 0.33) * FABRIC_CONTRAST;
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** A threshold with a soft shoulder, so a texture fades in rather than snapping. */
function above(v: number, cut: number, gain: number): number {
  return v <= cut ? 0 : Math.min(1, (v - cut) / (1 - cut)) * gain;
}

/**
 * How strongly this surface's grain marks this point, and which way.
 *
 * `a` is 0..1 of `FABRIC_MAX`; `lift` is how much of it *raises* the surface
 * rather than only shading it, in tiles. The lift is the piece that makes this
 * more than a stain: the fabric is added to the height field before the light
 * is computed, so a dune has a lit face and a shadowed one, and scree catches
 * the light on its plates. A darkening-only fabric on a lit ground is a decal;
 * a fabric that is part of the surface is a surface.
 *
 * `up` is the one flag ice needs and nothing else does — on snow the drift is
 * the bright part and the scour is the shadow, so the mark lightens.
 *
 * Every country is a *different kind* of mark rather than the same speckle at
 * different strengths. That is the test: a player should be able to tell a fen
 * from a dune field with the colour taken away.
 */
function fabricAt(
  kind: FabricKind,
  x: number,
  y: number,
  seed: number,
): { a: number; lift: number; up: boolean } {
  switch (kind) {
    case 'turf': {
      /**
       * Worked open country: a grain, and a field pattern under it. The plain
       * was the one country with nothing but even speckle, which at any zoom is
       * indistinguishable from no texture at all — the coarse ridged term is the
       * faint lineation of ground somebody has ploughed, and it is what says
       * people live here.
       */
      const grain = fbm(x, y, 0.72, seed + 97, 2);
      const fields = ridged(x * 0.9, y * 1.1, 0.22, seed + 103, 1);
      // The lift matters more than the stain here — see `FABRIC_MAX`. A worked
      // plain is gently corrugated, and it is the light on that corrugation
      // rather than a speckle that tells a player anybody farms it.
      return { a: above(grain, 0.5, 0.85) + above(fields, 0.74, 0.6), lift: 0.045, up: false };
    }
    case 'litter': {
      /**
       * The floor under a canopy: mould in clumps, broken by clearings.
       *
       * The clearing term is what stops a wood being a rectangle of stipple. A
       * forest has holes in it — a burn, a bog, a track — and a second, much
       * coarser noise punching those holes does more for "this is a forest"
       * than any amount of work on the individual leaves.
       */
      const mass = fbm(x, y, 0.55, seed + 17, 3);
      const clearing = vn(x, y, 0.16, seed + 23);
      const open = clearing < 0.36 ? (0.36 - clearing) * 2.2 : 0;
      return { a: Math.max(0, above(mass, 0.42, 1.35) - open), lift: 0.03, up: false };
    }
    case 'scree': {
      // Shattered rock: plates at two sizes, and the lift is what makes them
      // plates rather than spots.
      const plates = fbm(x, y, 0.62, seed + 67, 3);
      const shatter = ridged(x * 1.1, y * 0.9, 1.2, seed + 71, 1);
      return {
        a: above(plates, 0.44, 1.15) + above(shatter, 0.62, 0.5),
        lift: 0.055,
        up: false,
      };
    }
    case 'dune': {
      /**
       * Sand combed along one warped axis.
       *
       * The warp is the whole thing. Unwarped, a ridge across a plane is
       * corduroy; dragged sideways by a long-wavelength noise it becomes the
       * sinuous, branching crests a sand sea actually has. Two scales, because
       * a dune field has big dunes with a ripple running over their backs — and
       * the biggest lift in the table, because on sand the shape *is* the
       * surface and there is nothing else out there to carry it.
       */
      const warp = vn(x, y, 0.1, seed + 41) * 7;
      const axis = x * 0.72 + y * 0.28 + warp;
      const crest = ridged(axis, y * 0.35, 0.4, seed + 43, 2);
      const ripple = ridged(axis * 1.4, y * 0.5, 1.05, seed + 47, 1);
      return {
        a: above(crest, 0.3, 1.25) + above(ripple, 0.55, 0.4),
        lift: 0.11,
        up: false,
      };
    }
    case 'drift': {
      /**
       * Wind-packed snow. The one fabric that *lightens*: on snow the drift is
       * the bright part and the crack is the shadow, and inverting it is the
       * difference between a snowfield and a dirty one.
       */
      const packed = ridged(x * 0.8, y * 1.15, 0.34, seed + 71, 2);
      const scour = fbm(x, y, 0.9, seed + 73, 2);
      return {
        a: above(packed, 0.5, 1.1) + above(scour, 0.72, 0.4),
        lift: 0.07,
        up: true,
      };
    }
    case 'reed': {
      /**
       * Fen: standing water lies in the contour, so the grain runs flat and
       * braids. Sampled with y compressed four to one, which is what turns
       * isotropic noise into streaks without a second function.
       */
      const streak = ridged(x * 0.55, y * 2.2, 0.7, seed + 53, 2);
      const pools = vn(x, y, 0.28, seed + 59);
      return {
        a: above(streak, 0.42, 1.2) * (0.55 + pools * 0.75),
        lift: 0.015,
        up: false,
      };
    }
    case 'shingle': {
      // A beach is the smoothest ground on any map and should read that way:
      // fine, low, and running parallel to nothing in particular.
      const ripple = ridged(x * 1.3, y * 0.9, 1.0, seed + 101, 2);
      return { a: above(ripple, 0.55, 0.5), lift: 0.02, up: false };
    }
    case 'rubble': {
      // Heaps, and the coarse term under them is old plot boundaries — which is
      // the reason a ruin is not simply a rocky place.
      const heap = fbm(x, y, 0.95, seed + 83, 2);
      const plots = ridged(x * 0.8, y * 0.8, 0.24, seed + 89, 1);
      return { a: above(heap, 0.5, 1.0) + above(plots, 0.82, 0.55), lift: 0.045, up: false };
    }
    case 'current': {
      /**
       * Running water, and it is the only fabric drawn *across* the surface
       * rather than in it: the lift is nearly nothing, because a surface with a
       * relief in it is not water.
       */
      const flow = ridged(x * 0.5, y * 1.6, 0.85, seed + 131, 2);
      return { a: above(flow, 0.48, 0.7), lift: 0.006, up: true };
    }
    case 'plank': {
      // Decking. Boards run one way and the joints between them are the mark,
      // which is why this is the one fabric with no noise in it at all.
      const board = Math.abs(((y * 3.1) % 1) - 0.5) * 2;
      const grain = vn(x, y, 1.4, seed + 137);
      return { a: above(1 - board, 0.72, 1.1) + grain * 0.16, lift: 0.02, up: false };
    }
    case 'rut': {
      // A worked track: two ruts, the stones thrown out of them, and the mud
      // between. Low-frequency along the track and high across it.
      const ruts = ridged(x * 1.6, y * 1.6, 0.8, seed + 107, 2);
      const stones = fbm(x, y, 1.3, seed + 109, 1);
      return { a: above(ruts, 0.55, 0.75) + above(stones, 0.66, 0.45), lift: 0.03, up: false };
    }
    default: {
      // Ash and cinder: the baseline. Blotchy, directionless, and quiet.
      const grit = fbm(x, y, 0.8, seed + 7, 3);
      return { a: above(grit, 0.5, 0.8), lift: 0.025, up: false };
    }
  }
}

/**
 * The country's own relief, in tiles, at a tile centre.
 *
 * Two octaves and no more. The first is the shape of the ground at the scale of
 * a district — nine tiles, which is about a third of a town — and the second is
 * the break in it at three. Anything finer belongs to the fabric, which is
 * sampled four times as often and can afford it.
 *
 * Ridged rather than plain fbm for the coarse term, because what makes high
 * ground read as high ground is that it has *edges*: a lit face, a crest, a
 * shadowed face. Smooth noise lit from any angle stays a rounded blob, which is
 * the finding the campaign chart spent two attempts on.
 */
function reliefAt(x: number, y: number, seed: number): number {
  const coarse = 1 - Math.abs(vn(x, y, 0.11, seed + 907) - 0.5) * 2;
  const fine = vn(x, y, 0.3, seed + 911);
  return (coarse * coarse - 0.33) * 0.8 + (fine - 0.5) * 0.45;
}

/**
 * How much of a tile of battlefield relief goes into the form layer's height.
 *
 * `World.heightGrid` counts in thirty-seconds of a tile and the surface table's
 * own `rise` runs about −0.5 to +0.5, so the constant is chosen to put a real
 * ridge — two and a half tiles at the crest over a twenty-tile shoulder —
 * within a hair of the same *gradient* per tile as the edge of a stone shelf.
 * Matching the gradient rather than the amplitude is the point: the lamp reads
 * slope, and a broad rise that arrived at shelf amplitude over twenty tiles
 * would be shaded as though it were flat.
 */
const RELIEF_PER_UNIT = 1.6 / 32;

/** Everything the bake needs that is not the chunk it is baking. */
export interface GroundField {
  /** The map's terrain array, read only. */
  terrain: Uint8Array;
  mapW: number;
  mapH: number;
  theme: BiomeTheme;
  /** The world's own seed, so two settlements do not share one hillside. */
  seed: number;
  /**
   * The battlefield's real relief, one byte a tile — `World.heightGrid`, or
   * null on every world that has none, which is every settlement.
   *
   * A settlement's ground has no elevation array and does not need one: this
   * file's whole opening argument is that twelve terrain classes *are* twelve
   * statements about height. A battlefield is the one place that stops being
   * true, because a ridge is not a terrain class — the same ash plain is
   * twenty feet higher on one side of the field than the other, and if the
   * form layer cannot say so then the player is being asked to charge a hill
   * they cannot see. It rides into `tH` beside the terrain's own rise and is
   * lit by the same lamp, so a crest takes the north-west light and its far
   * face goes into shade exactly as a stone shelf does.
   */
  height?: Uint8Array | null;
}

/**
 * The bake, with its working buffers held between chunks.
 *
 * A class rather than a function because a chunk bake allocates about fifteen
 * typed arrays and a full re-bake does a dozen chunks in one frame — which is
 * a hundred and eighty allocations and a garbage collection landing in the
 * middle of a siege. They are sized once, from the chunk size, and reused.
 */
export class GroundBaker {
  /** Tiles per chunk side. */
  readonly chunk: number;
  /** Ground pixels per chunk side. */
  readonly px: number;
  /** Tile-grid side, chunk plus margin on both sides. */
  private readonly g: number;

  // Per-tile, raw.
  private readonly tR: Float32Array;
  private readonly tG: Float32Array;
  private readonly tB: Float32Array;
  private readonly tH: Float32Array;
  private readonly tHard: Float32Array;
  private readonly tFab: Uint8Array;
  private readonly tGain: Float32Array;

  // Per-tile, blurred.
  private readonly bR: Float32Array;
  private readonly bG: Float32Array;
  private readonly bB: Float32Array;
  private readonly bH: Float32Array;

  // Per ground pixel.
  private readonly pH: Float32Array;
  private readonly pR: Float32Array;
  private readonly pG: Float32Array;
  private readonly pB: Float32Array;
  private readonly pFab: Float32Array;
  private readonly pUp: Uint8Array;

  /** The fabric kinds in play, indexed by the byte in `tFab`. */
  private fabKinds: FabricKind[] = [];

  constructor(chunkTiles: number) {
    this.chunk = chunkTiles;
    this.px = chunkTiles * GROUND_SUB;
    this.g = chunkTiles + MARGIN * 2;
    const n = this.g * this.g;
    const m = this.px * this.px;
    this.tR = new Float32Array(n);
    this.tG = new Float32Array(n);
    this.tB = new Float32Array(n);
    this.tH = new Float32Array(n);
    this.tHard = new Float32Array(n);
    this.tFab = new Uint8Array(n);
    this.tGain = new Float32Array(n);
    this.bR = new Float32Array(n);
    this.bG = new Float32Array(n);
    this.bB = new Float32Array(n);
    this.bH = new Float32Array(n);
    this.pH = new Float32Array(m);
    this.pR = new Float32Array(m);
    this.pG = new Float32Array(m);
    this.pB = new Float32Array(m);
    this.pFab = new Float32Array(m);
    this.pUp = new Uint8Array(m);
  }

  /**
   * Bake one chunk's ground into `out`, which must hold `px * px` RGBA pixels.
   *
   * `cx`/`cy` are chunk coordinates; the tiles read run from `cx*chunk - 2` to
   * `cx*chunk + chunk + 1`, clamped to the map, so a chunk on the map's edge
   * repeats its border tile rather than inventing one — which is invisible,
   * because the map's edge is off the playable ground anyway.
   */
  bake(f: GroundField, cx: number, cy: number, out: Uint8ClampedArray): void {
    const surf = surfaceTable(f.theme);
    this.fabKinds = [];
    const fabIndex = new Map<FabricKind, number>();
    const indexOf = (k: FabricKind): number => {
      let i = fabIndex.get(k);
      if (i === undefined) {
        i = this.fabKinds.length;
        this.fabKinds.push(k);
        fabIndex.set(k, i);
      }
      return i;
    };

    const G = this.g;
    const x0 = cx * this.chunk - MARGIN;
    const y0 = cy * this.chunk - MARGIN;
    const { terrain, mapW, mapH, theme, seed } = f;
    const height = f.height ?? null;
    const fallback = surf[Terrain.AshPlain];

    // ── 1. The tile grid ───────────────────────────────────────────────────
    for (let j = 0; j < G; j++) {
      const ty = Math.min(mapH - 1, Math.max(0, y0 + j));
      for (let i = 0; i < G; i++) {
        const tx = Math.min(mapW - 1, Math.max(0, x0 + i));
        const s: Surface = surf[terrain[ty * mapW + tx]] ?? fallback;
        const k = j * G + i;
        this.tR[k] = (s.base >> 16) & 0xff;
        this.tG[k] = (s.base >> 8) & 0xff;
        this.tB[k] = s.base & 0xff;
        // The country's relief rides the terrain's own rise, scaled by how
        // broken that surface is — so a fen stays flat while the shelf beside
        // it breaks up, on the same map and from the same noise.
        this.tH[k] = s.rise + reliefAt(tx, ty, seed) * s.rough * theme.relief;
        if (height !== null) this.tH[k] += height[ty * mapW + tx] * RELIEF_PER_UNIT;
        this.tHard[k] = s.hard;
        this.tFab[k] = indexOf(s.fabric);
        this.tGain[k] = s.fabricGain;
      }
    }

    // ── 2. The blur, and the hardness that survives it ─────────────────────
    /**
     * A weighted 3×3, once.
     *
     * This is the whole of "the edge is a transition", and the weights are the
     * one number in this file that had to be measured against a picture rather
     * than argued. The first cut used the usual 1-2-1 separable kernel — centre
     * weight 4 of 16 — which spreads a boundary over about a tile and a half
     * once the smooth interpolation below is applied on top, and a tile and a
     * half is **too far**: `cityart-01-hold-plain-z30` came out as warm fog with
     * a road smeared through it and a stone shelf reduced to a pale haze. A
     * transition that erases what it is a transition between has stopped being
     * a transition.
     *
     * Centre 8 of 20 instead — 40% rather than 25% — which lands the boundary at
     * roughly a tile. Far enough that grass shades into forest floor and there
     * is no grid; near enough that a two-tile road is still a road.
     *
     * Then the hardness is given back. A road is two tiles wide and a blur that
     * treats it like a hillside erases it; a bridge is a *structure* and has an
     * edge by definition. So each surface says how much of itself it keeps, and
     * the natural ground — which is all the ask was ever about — keeps almost
     * none, which is exactly what makes it blend.
     */
    for (let j = 1; j < G - 1; j++) {
      for (let i = 1; i < G - 1; i++) {
        const k = j * G + i;
        let r = 0;
        let g = 0;
        let b = 0;
        let h = 0;
        let wsum = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            const w = dx === 0 && dy === 0 ? 8 : dx === 0 || dy === 0 ? 2 : 1;
            const kk = k + dy * G + dx;
            r += this.tR[kk] * w;
            g += this.tG[kk] * w;
            b += this.tB[kk] * w;
            h += this.tH[kk] * w;
            wsum += w;
          }
        }
        const hard = this.tHard[k];
        this.bR[k] = (r / wsum) * (1 - hard) + this.tR[k] * hard;
        this.bG[k] = (g / wsum) * (1 - hard) + this.tG[k] * hard;
        this.bB[k] = (b / wsum) * (1 - hard) + this.tB[k] * hard;
        // Height keeps less of its hardness than colour does: a ditch must stay
        // a hole, but a hole with vertical walls is a hole nothing can be lit
        // against.
        const hh = hard * 0.55;
        this.bH[k] = (h / wsum) * (1 - hh) + this.tH[k] * hh;
      }
    }

    // ── 3. Colour, height and fabric, per ground pixel ─────────────────────
    const P = this.px;
    const inv = 1 / GROUND_SUB;
    for (let py = 0; py < P; py++) {
      // Tile coordinate of this pixel's centre, in the tile grid's own frame.
      const fy = MARGIN + (py + 0.5) * inv;
      const gy = fy - 0.5;
      const j0 = Math.floor(gy);
      const ry = gy - j0;
      const sy = ry * ry * (3 - 2 * ry);
      const worldY = y0 + fy;
      for (let px = 0; px < P; px++) {
        const fx = MARGIN + (px + 0.5) * inv;
        const gx = fx - 0.5;
        const i0 = Math.floor(gx);
        const rx = gx - i0;
        /**
         * Smoothstepped rather than linear, and it is not cosmetic.
         *
         * Bilinear interpolation is only C0: its gradient is constant inside a
         * cell and jumps at every boundary, so a hillshade taken from it comes
         * out as flat-lit squares one tile across — the exact tiled-floor look
         * this pass exists to remove, reintroduced by the lighting. Smoothstep
         * is C1, its gradient varies continuously, and it costs two multiplies.
         */
        const sx = rx * rx * (3 - 2 * rx);
        const a = j0 * G + i0;
        const bIdx = a + 1;
        const c = a + G;
        const d = c + 1;
        const k = py * P + px;
        this.pR[k] =
          (this.bR[a] + (this.bR[bIdx] - this.bR[a]) * sx) * (1 - sy) +
          (this.bR[c] + (this.bR[d] - this.bR[c]) * sx) * sy;
        this.pG[k] =
          (this.bG[a] + (this.bG[bIdx] - this.bG[a]) * sx) * (1 - sy) +
          (this.bG[c] + (this.bG[d] - this.bG[c]) * sx) * sy;
        this.pB[k] =
          (this.bB[a] + (this.bB[bIdx] - this.bB[a]) * sx) * (1 - sy) +
          (this.bB[c] + (this.bB[d] - this.bB[c]) * sx) * sy;
        const h =
          (this.bH[a] + (this.bH[bIdx] - this.bH[a]) * sx) * (1 - sy) +
          (this.bH[c] + (this.bH[d] - this.bH[c]) * sx) * sy;

        /**
         * The fabric is the nearest tile's, and its strength is eased by how
         * much of the neighbourhood agrees with it.
         *
         * Blending the *mark* across a boundary as well as the colour would
         * mean evaluating two grains per pixel for a difference nothing can
         * see at this alpha. Easing it instead costs one comparison and does
         * the job the eye actually notices: a lone tile of forest in open
         * country gets a whisper of leaf mould rather than a hard-edged square
         * of it, and the middle of a wood gets the lot.
         */
        const ti = Math.round(gx);
        const tj = Math.round(gy);
        const tk = tj * G + ti;
        const mine = this.tFab[tk];
        let agree = 1;
        if (this.tFab[tk - 1] === mine) agree++;
        if (this.tFab[tk + 1] === mine) agree++;
        if (this.tFab[tk - G] === mine) agree++;
        if (this.tFab[tk + G] === mine) agree++;
        const fab = fabricAt(this.fabKinds[mine], x0 + fx, worldY, seed);
        const gain = this.tGain[tk] * theme.grain * (0.45 + 0.55 * ((agree - 1) / 4));
        const av = Math.min(1, fab.a * gain);
        this.pFab[k] = av;
        this.pUp[k] = fab.up ? 1 : 0;
        this.pH[k] = h + fab.lift * av * (fab.up ? 1 : -1);
      }
    }

    // ── 4. The light ───────────────────────────────────────────────────────
    /**
     * Central differences on the finished surface, against the light, as a
     * plain dot product: `1/sqrt(gx²+gy²+1)` is the normal's z and the light
     * has no z term because a grazing light is what shows form — a lamp
     * directly overhead lights a mountain and a marsh identically.
     */
    const push = GROUND_SUB * EXAGGERATION;
    const key = theme.key;
    // The lamp, brought inside the range the ground is allowed to occupy. See
    // `SUN_MAX_LUMA` — this one line is what stops every steep face in the
    // country from arriving at the ceiling together.
    const sun = capLuma(theme.sun, SUN_MAX_LUMA);
    const shadow = theme.shadow;
    for (let py = 0; py < P; py++) {
      for (let px = 0; px < P; px++) {
        const k = py * P + px;
        const l = this.pH[k - (px > 0 ? 1 : 0)];
        const r = this.pH[k + (px < P - 1 ? 1 : 0)];
        const u = this.pH[k - (py > 0 ? P : 0)];
        const dn = this.pH[k + (py < P - 1 ? P : 0)];
        const dx = (r - l) * 0.5 * push;
        const dy = (dn - u) * 0.5 * push;
        const nz = 1 / Math.sqrt(dx * dx + dy * dy + 1);
        const lit = (-dx * LIGHT_X - dy * LIGHT_Y) * nz;

        /**
         * The ceiling on how far a pixel may be pushed toward the light or the
         * dark, and it is 0.5 rather than the 0.72 the first cut used.
         *
         * At 0.72 a pixel on a steep face is nearly the colour of the *lamp*
         * rather than the colour of the ground — which is what put the smoke on
         * the river bank and the mottle on the massif. A hillshade's job is to
         * say which way a surface faces, and half the distance says it just as
         * clearly while leaving the surface its own colour.
         */
        const col = mix(
          ((this.pR[k] & 0xff) << 16) | ((this.pG[k] & 0xff) << 8) | (this.pB[k] & 0xff),
          lit >= 0 ? sun : shadow,
          Math.min(0.5, Math.abs(lit) * key),
        );
        let rr = (col >> 16) & 0xff;
        let gg = (col >> 8) & 0xff;
        let bb = col & 0xff;

        /**
         * The grain, last.
         *
         * It only ever darkens unless the country says otherwise — a canopy, a
         * dune shadow and a tussock are all things that sit on the ground and
         * cast on it. Snow is the exception: on snow the drift is the bright
         * part and the scour is the shadow.
         *
         * **A lightening grain brightens the pixel it is on rather than
         * blending toward the sun**, and the difference is not cosmetic. Blended
         * toward a light of luma 240 the drift took 47% of a snowfield straight
         * through the cap, measured — a flat white sheet with no variation left
         * in it, which is exactly the saturation failure the campaign chart's
         * hillshade spent two attempts on. A proportional lift cannot do that:
         * a dark pixel gets a small one and a bright pixel a proportionate one,
         * so the grain survives wherever the ground happens to sit.
         */
        const fa = this.pFab[k] * FABRIC_MAX;
        if (fa > 0) {
          if (this.pUp[k] === 1) {
            const m = 1 + fa * 0.7;
            rr *= m;
            gg *= m;
            bb *= m;
          } else {
            rr += (((shadow >> 16) & 0xff) - rr) * fa;
            gg += (((shadow >> 8) & 0xff) - gg) * fa;
            bb += ((shadow & 0xff) - bb) * fa;
          }
        }

        // The cap, on the finished pixel. See `TERRAIN_MAX_LUMA`: a base colour
        // under it can be lit straight through it, which is how a snowfield
        // would otherwise have got out.
        const lm = 0.299 * rr + 0.587 * gg + 0.114 * bb;
        const s = lm > TERRAIN_MAX_LUMA ? TERRAIN_MAX_LUMA / lm : 1;
        const o = k * 4;
        out[o] = rr * s;
        out[o + 1] = gg * s;
        out[o + 2] = bb * s;
        out[o + 3] = 255;
      }
    }
  }
}
